import clsx from 'clsx';
import isUndefined from 'lodash/isUndefined';
import React from 'react';

import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import CancelIcon from '@material-ui/icons/Cancel';
import RemoveIcon from '@material-ui/icons/DeleteOutlined';

import { useStyles } from './styles';

export type ComponentProps = {
	open: boolean;
	title?: string | JSX.Element;
	content?: string | JSX.Element;
	additionalContent?: JSX.Element;
	dismissText?: string;
	confirmText: string;
	confirmTypeButton: 'confirm' | 'remove';
	confirmButtonDisabled?: boolean;
	loading?: boolean;
	onDismiss?(): void;
	onConfirm(): void;
};

const DialogConfirm = (props: ComponentProps) => {
	const classes = useStyles();

	const { open, title, content, additionalContent, dismissText, confirmText, confirmTypeButton, confirmButtonDisabled, loading } = props;
	const { onDismiss, onConfirm } = props;

	const [isDoingAction, setIsDoingAction] = React.useState(false);

	const controlled = !isUndefined(loading);
	const isButtonDisplayed = () => (controlled ? !loading : !isDoingAction);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	React.useEffect(() => setIsDoingAction(false), [open]);
	return (
		<Dialog
			classes={{
				paper: classes.root
			}}
			disableBackdropClick
			disableEscapeKeyDown
			maxWidth="xs"
			open={open}
		>
			<IconButton className={classes.cancelIcon} size="small" onClick={onDismiss} data-testid="confirmDialogCancelIcon">
				<CancelIcon fontSize="inherit" />
			</IconButton>
			<DialogTitle className={classes.title} disableTypography>
				{title}
			</DialogTitle>
			<DialogContent className={classes.content}>{content}</DialogContent>
			<DialogActions className={classes.actionsContainer}>
				{additionalContent ? <div className={classes.additionalContentWrapper}>{additionalContent}</div> : null}
				<Button variant="outlined" classes={{ label: classes.buttonLabel }} onClick={onDismiss} data-testid="confirmDialogDismiss">
					{dismissText}
				</Button>
				{isButtonDisplayed() ? (
					<Button
						className={classes[`confirmButtonMode${confirmTypeButton}`]}
						classes={{
							label: clsx(classes.buttonLabel, {
								[classes.confirmButtonLabel]: confirmTypeButton === 'confirm',
								[classes.removeButtonLabel]: confirmTypeButton === 'remove'
							})
						}}
						variant="contained"
						onClick={() => {
							setIsDoingAction(true);
							onConfirm();
						}}
						startIcon={confirmTypeButton === 'remove' ? <RemoveIcon /> : null}
						data-testid="confirmDialogConfirm"
						disabled={confirmButtonDisabled}
					>
						{confirmText}
					</Button>
				) : (
					<CircularProgress />
				)}
			</DialogActions>
		</Dialog>
	);
};

export default React.memo(DialogConfirm);
